import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCountdown } from 'usehooks-ts';

import { CANCEL_ROUND, NO_MORE_BETS, PLACE_YOUR_BETS } from '@ezugi/constants';
import { RoundStatus } from '@ezugi/types';

import { gameTimerSelector, roundStatusSelector } from '../store/selectors';

export type GameTimerProps = {
  startStates?: RoundStatus[],
  stopStates?: RoundStatus[],
}

export function useGameTimer(
  {
    startStates = [ PLACE_YOUR_BETS ],
    stopStates = [ NO_MORE_BETS, CANCEL_ROUND ],
  } = {},
) {
  const timer = useSelector(gameTimerSelector);
  const { totalTime = 0, timeLeft = 0, messageTimestamp = Date.now() } = timer;

  const [ isCountdownRunning, setIsCountdownRunning ] = useState(false);

  const gameStatus = useSelector(roundStatusSelector);

  const countStart = useMemo(
    () => timeLeft - Math.floor((Date.now() - messageTimestamp) / 1000),
    [ timeLeft, messageTimestamp ]
  );
  const [ , { stopCountdown, startCountdown, resetCountdown } ] = useCountdown({
    countStart,
    intervalMs: 1000,
  });

  useEffect(() => {
    if (stopStates.includes(gameStatus)) {
      stopCountdown();
      setIsCountdownRunning(false);
    }
  }, [ stopStates, gameStatus, stopCountdown ]);

  useEffect(() => {
    if (startStates.includes(gameStatus)) {
      resetCountdown();
      startCountdown();
      setIsCountdownRunning(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ timeLeft, gameStatus ]);

  const timeLeftMs = (countStart - Math.floor((Date.now() - messageTimestamp) / 1000)) * 1000;
  return {
    ...timer,
    totalTime: totalTime * 1000,
    timeLeft: isCountdownRunning ? Math.max(timeLeftMs, 0) : 0,
    isCountdownRunning,
  };
}
